/**
 * Annotation editor for a captured screenshot (CoPaya flow).
 *
 * The screenshot stays untouched in its own canvas; annotations live as
 * vector ops replayed over it (see utils/annotations.ts), which is what makes
 * undo/clear trivial. The visible canvas keeps the capture's device-pixel
 * size and is scaled by CSS — `toImagePoint` is the only place the two
 * coordinate spaces meet.
 */

import { useCallback, useEffect, useReducer, useRef, useState } from 'react';
import {
  Box,
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputBase,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  useTheme,
} from '@mui/material';
import GestureIcon from '@mui/icons-material/Gesture';
import CropSquareIcon from '@mui/icons-material/CropSquare';
import NorthEastIcon from '@mui/icons-material/NorthEast';
import TextFieldsIcon from '@mui/icons-material/TextFields';
import UndoIcon from '@mui/icons-material/Undo';
import DeleteSweepIcon from '@mui/icons-material/DeleteSweep';
import { useTranslation } from 'react-i18next';
import { common } from '@mui/material/colors';
import { ResponsiveDialog } from '@/components/common/ResponsiveDialog';
import { useIsMobile } from '@/hooks/useIsMobile';
import { chartColors } from '@/styles/theme';
import {
  annotationsReducer,
  clampSummary,
  drawOps,
  exportAnnotatedPng,
  SUMMARY_MAX_LENGTH,
  type AnnotationOp,
  type AnnotationTool,
  type Point,
} from '@/utils/annotations';
import type { CapturedImage } from '@/utils/screenshot';

/** Base sizes in CSS px; multiplied by the capture scale per op. */
const BASE_STROKE_WIDTH = 3;
const BASE_FONT_SIZE = 16;
/** Shapes smaller than this (CSS px) on pointerup are discarded as accidents. */
const MIN_SHAPE_PX = 2;

// Module scope — no hook available; labelKey pattern per project convention.
// Values come from theme/MUI tokens, never hex literals (CI gate).
const ANNOTATION_COLORS = [
  { labelKey: 'colors.red', value: chartColors[3] },
  { labelKey: 'colors.blue', value: chartColors[0] },
  { labelKey: 'colors.green', value: chartColors[1] },
  { labelKey: 'colors.yellow', value: chartColors[2] },
  { labelKey: 'colors.white', value: common.white },
] as const;

const TOOLS: ReadonlyArray<{ tool: AnnotationTool; labelKey: string; Icon: typeof GestureIcon }> =
  [
    { tool: 'pen', labelKey: 'tools.pen', Icon: GestureIcon },
    { tool: 'rect', labelKey: 'tools.rect', Icon: CropSquareIcon },
    { tool: 'arrow', labelKey: 'tools.arrow', Icon: NorthEastIcon },
    { tool: 'text', labelKey: 'tools.text', Icon: TextFieldsIcon },
  ];

interface PendingText {
  image: Point;
  /** Position and font size in the canvas's CSS space, fixed at click time. */
  cssX: number;
  cssY: number;
  cssFontSize: number;
}

interface ScreenshotAnnotationDialogProps {
  open: boolean;
  screenshot: CapturedImage | null;
  /** Entry-link request in flight — disables Send. */
  isSending: boolean;
  onClose: () => void;
  /**
   * Called synchronously inside the Send click gesture; `pngBlob` is the
   * still-encoding PNG so the caller can hand it straight to the clipboard.
   */
  onSend: (args: { pngBlob: Promise<Blob>; summary: string }) => void;
}

export function ScreenshotAnnotationDialog({
  open,
  screenshot,
  isSending,
  onClose,
  onSend,
}: ScreenshotAnnotationDialogProps) {
  const { t } = useTranslation('copaya');
  const { t: tc } = useTranslation('common');
  const theme = useTheme();
  const isMobile = useIsMobile();
  const fontFamily = theme.typography.fontFamily ?? 'sans-serif';

  const [ops, dispatch] = useReducer(annotationsReducer, []);
  const [tool, setTool] = useState<AnnotationTool>('pen');
  const [color, setColor] = useState<string>(ANNOTATION_COLORS[0].value);
  const [summary, setSummary] = useState('');
  const [pendingText, setPendingText] = useState<PendingText | null>(null);
  const [pendingValue, setPendingValue] = useState('');

  const canvasRef = useRef<HTMLCanvasElement>(null);
  const draftRef = useRef<AnnotationOp | null>(null);

  // Fresh editor per opening: the previous screenshot's annotations must not
  // bleed into the next one.
  useEffect(() => {
    if (!open) return;
    dispatch({ type: 'clear' });
    setTool('pen');
    setColor(ANNOTATION_COLORS[0].value);
    setSummary('');
    setPendingText(null);
    setPendingValue('');
    draftRef.current = null;
  }, [open]);

  const redraw = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas || !screenshot) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(screenshot.canvas, 0, 0);
    const draft = draftRef.current;
    drawOps(ctx, draft ? [...ops, draft] : ops, fontFamily);
  }, [ops, screenshot, fontFamily]);

  useEffect(() => {
    redraw();
  }, [redraw]);

  // Latest redraw for the ref callback below, which must stay
  // identity-stable so React doesn't detach/reattach the canvas per render.
  const redrawRef = useRef(redraw);
  redrawRef.current = redraw;

  // MUI's Portal mounts the dialog contents one commit AFTER the dialog's
  // own effects have run, so the effect above finds canvasRef.current null
  // on open and the screenshot stayed blank until the first interaction
  // redrew it. Drawing from the ref callback runs exactly when the canvas
  // element actually attaches.
  const attachCanvas = useCallback((node: HTMLCanvasElement | null) => {
    canvasRef.current = node;
    if (node) redrawRef.current();
  }, []);

  // pointermove can outpace the frame rate (and each redraw blits the whole
  // viewport-sized bitmap), so drag feedback is coalesced to one redraw per
  // animation frame. Commits keep drawing through the effect above.
  const redrawQueuedRef = useRef(false);
  const requestRedraw = useCallback(() => {
    if (redrawQueuedRef.current) return;
    redrawQueuedRef.current = true;
    requestAnimationFrame(() => {
      redrawQueuedRef.current = false;
      // Skip the stale frame when the draft was committed (or cancelled)
      // between queueing and firing — the ops effect has taken over.
      if (draftRef.current) redraw();
    });
  }, [redraw]);

  const toImagePoint = (e: React.PointerEvent<HTMLCanvasElement>): Point => {
    const canvas = e.currentTarget;
    const r = canvas.getBoundingClientRect();
    return {
      x: (e.clientX - r.left) * (canvas.width / r.width),
      y: (e.clientY - r.top) * (canvas.height / r.height),
    };
  };

  /** The open text box as an op, or null when there is none / it is empty. */
  const buildPendingTextOp = (): AnnotationOp | null => {
    if (!pendingText || !screenshot) return null;
    const text = pendingValue.trim();
    if (!text) return null;
    return {
      tool: 'text',
      color,
      fontSize: BASE_FONT_SIZE * screenshot.scale,
      x: pendingText.image.x,
      y: pendingText.image.y,
      text,
    };
  };

  // Idempotent: a no-op once pendingText is null, so the input's blur firing
  // (or not firing — platforms differ) after an explicit commit is harmless.
  const commitPendingText = () => {
    const op = buildPendingTextOp();
    if (op) dispatch({ type: 'add', op });
    setPendingText(null);
    setPendingValue('');
  };

  const handlePointerDown = (e: React.PointerEvent<HTMLCanvasElement>) => {
    if (!screenshot) return;
    if (e.pointerType === 'mouse' && e.button !== 0) return;
    const point = toImagePoint(e);

    if (tool === 'text') {
      // A click while a text box is open commits and closes it — done here
      // explicitly because not every platform blurs the input on a canvas
      // click (iOS Safari). The next click places a new box.
      if (pendingText) {
        commitPendingText();
        return;
      }
      // The box mounts and autofocuses during this pointerdown, but the
      // browser's default action (focus fixup for a press on a non-focusable
      // canvas) runs after the handlers and would blur it in the same frame —
      // the blur commit then closes the empty box before a single keystroke.
      // Canceling pointerdown suppresses that default action.
      e.preventDefault();
      const r = e.currentTarget.getBoundingClientRect();
      const cssScale = r.width / e.currentTarget.width;
      setPendingText({
        image: point,
        cssX: e.clientX - r.left,
        cssY: e.clientY - r.top,
        cssFontSize: BASE_FONT_SIZE * screenshot.scale * cssScale,
      });
      return;
    }

    e.currentTarget.setPointerCapture(e.pointerId);
    const strokeWidth = BASE_STROKE_WIDTH * screenshot.scale;
    draftRef.current =
      tool === 'pen'
        ? { tool: 'pen', color, strokeWidth, points: [point] }
        : tool === 'rect'
          ? { tool: 'rect', color, strokeWidth, x: point.x, y: point.y, width: 0, height: 0 }
          : { tool: 'arrow', color, strokeWidth, from: point, to: point };
    redraw();
  };

  const handlePointerMove = (e: React.PointerEvent<HTMLCanvasElement>) => {
    const draft = draftRef.current;
    if (!draft) return;
    const point = toImagePoint(e);
    switch (draft.tool) {
      case 'pen':
        draft.points.push(point);
        break;
      case 'rect':
        draft.width = point.x - draft.x;
        draft.height = point.y - draft.y;
        break;
      case 'arrow':
        draft.to = point;
        break;
      case 'text':
        break;
    }
    requestRedraw();
  };

  const handlePointerUp = (e: React.PointerEvent<HTMLCanvasElement>) => {
    const draft = draftRef.current;
    draftRef.current = null;
    if (!draft || !screenshot) return;
    e.currentTarget.releasePointerCapture(e.pointerId);

    const minPx = MIN_SHAPE_PX * screenshot.scale;
    const keep =
      draft.tool === 'pen'
        ? draft.points.length >= 2
        : draft.tool === 'rect'
          ? Math.abs(draft.width) >= minPx || Math.abs(draft.height) >= minPx
          : draft.tool === 'arrow'
            ? Math.hypot(draft.to.x - draft.from.x, draft.to.y - draft.from.y) >= minPx
            : false;

    if (keep) {
      dispatch({ type: 'add', op: draft });
    } else {
      redraw();
    }
  };

  const handlePointerCancel = () => {
    draftRef.current = null;
    redraw();
  };

  const handleSendClick = () => {
    if (!screenshot) return;
    // An open text box is folded in here directly: relying on its blur to
    // have committed it first is not portable (iOS Safari does not blur on
    // button taps). When blur did fire, pendingText is already null and this
    // adds nothing twice.
    const pendingOp = buildPendingTextOp();
    const finalOps = pendingOp ? [...ops, pendingOp] : ops;
    // Synchronous flatten; only PNG encoding is promised — the caller needs
    // the promise while this click gesture is still live.
    const pngBlob = exportAnnotatedPng(screenshot.canvas, finalOps, fontFamily);
    onSend({ pngBlob, summary: clampSummary(summary) });
  };

  return (
    <ResponsiveDialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      mobileTitle={t('editorTitle')}
    >
      {!isMobile && <DialogTitle>{t('editorTitle')}</DialogTitle>}
      <DialogContent>
        <Stack spacing={2}>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
            <ToggleButtonGroup
              size="small"
              exclusive
              value={tool}
              onChange={(_e, next: AnnotationTool | null) => {
                if (next) setTool(next);
              }}
            >
              {TOOLS.map(({ tool: value, labelKey, Icon }) => (
                <ToggleButton key={value} value={value} aria-label={t(labelKey)}>
                  <Tooltip title={t(labelKey)}>
                    <Icon fontSize="small" />
                  </Tooltip>
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
            <Stack direction="row" spacing={0.5}>
              {ANNOTATION_COLORS.map(({ labelKey, value }) => (
                <Tooltip key={value} title={t(labelKey)}>
                  <IconButton
                    size="small"
                    aria-label={t(labelKey)}
                    aria-pressed={color === value}
                    onClick={() => setColor(value)}
                  >
                    <Box
                      sx={{
                        width: 18,
                        height: 18,
                        borderRadius: '50%',
                        backgroundColor: value,
                        border: '2px solid',
                        // 'divider' rather than transparent when unselected:
                        // the white swatch needs an outline to exist at all
                        // against the dialog background.
                        borderColor: color === value ? 'text.primary' : 'divider',
                      }}
                    />
                  </IconButton>
                </Tooltip>
              ))}
            </Stack>
            <Box sx={{ flexGrow: 1 }} />
            <Tooltip title={t('tools.undo')}>
              <span>
                <IconButton
                  size="small"
                  aria-label={t('tools.undo')}
                  disabled={ops.length === 0}
                  onClick={() => dispatch({ type: 'undo' })}
                >
                  <UndoIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title={t('tools.clear')}>
              <span>
                <IconButton
                  size="small"
                  aria-label={t('tools.clear')}
                  disabled={ops.length === 0}
                  onClick={() => dispatch({ type: 'clear' })}
                >
                  <DeleteSweepIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          </Stack>

          <Box
            sx={{
              position: 'relative',
              alignSelf: 'center',
              maxWidth: '100%',
              // The border lives on the wrapper: getBoundingClientRect on the
              // canvas must be pure content box or every pointer→image
              // mapping picks up the border as offset and scale error.
              border: '1px solid',
              borderColor: 'divider',
              lineHeight: 0,
            }}
          >
            {screenshot && (
              <canvas
                ref={attachCanvas}
                width={screenshot.canvas.width}
                height={screenshot.canvas.height}
                onPointerDown={handlePointerDown}
                onPointerMove={handlePointerMove}
                onPointerUp={handlePointerUp}
                onPointerCancel={handlePointerCancel}
                onMouseDown={(e) => {
                  // Belt to the pointerdown preventDefault above: the focus
                  // fixup is formally mousedown's default action, and not
                  // every browser lets a canceled pointerdown suppress it.
                  if (tool === 'text') e.preventDefault();
                }}
                style={{
                  display: 'block',
                  maxWidth: '100%',
                  maxHeight: '60vh',
                  width: 'auto',
                  height: 'auto',
                  touchAction: 'none',
                  cursor: tool === 'text' ? 'text' : 'crosshair',
                }}
              />
            )}
            {pendingText && (
              <InputBase
                autoFocus
                value={pendingValue}
                placeholder={t('textPlaceholder')}
                onChange={(e) => setPendingValue(e.target.value)}
                onBlur={commitPendingText}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    commitPendingText();
                  } else if (e.key === 'Escape') {
                    // The cancel belongs to the text box, not the dialog.
                    e.stopPropagation();
                    setPendingText(null);
                    setPendingValue('');
                  }
                }}
                sx={{
                  position: 'absolute',
                  left: pendingText.cssX,
                  top: pendingText.cssY,
                  fontSize: pendingText.cssFontSize,
                  lineHeight: 1,
                  color: color,
                  p: 0,
                  '& .MuiInputBase-input': { p: 0 },
                }}
              />
            )}
          </Box>

          <TextField
            size="small"
            fullWidth
            label={t('summaryLabel')}
            helperText={t('summaryHelper')}
            value={summary}
            onChange={(e) => setSummary(e.target.value)}
            slotProps={{ htmlInput: { maxLength: SUMMARY_MAX_LENGTH } }}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{tc('cancel')}</Button>
        <Button variant="contained" onClick={handleSendClick} disabled={isSending || !screenshot}>
          {t('send')}
        </Button>
      </DialogActions>
    </ResponsiveDialog>
  );
}
